import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  EyeIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  CheckIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { VersionInfo } from '../../utils/version';
import { useRegisterMutation, UserRole } from '../../store/slices/authSlice';

// 密码强度检查函数
const checkPasswordStrength = (password: string) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const passedCount = Object.values(checks).filter(Boolean).length;
  const strength = passedCount === 5 ? 100 : passedCount * 20;

  return { checks, strength, passedCount };
};

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [register, { isLoading }] = useRegisterMutation();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    agreeToTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    // 表单验证
    if (!formData.contactName.trim()) {
      setErrorMessage(t('auth:register.contactName.required'));
      return;
    }
    if (!formData.email.trim()) {
      setErrorMessage(t('auth:register.email.required'));
      return;
    }
    if (!formData.password) {
      setErrorMessage(t('auth:register.password.required'));
      return;
    }

    // 密码强度验证
    const { checks } = checkPasswordStrength(formData.password);
    const errors = [];
    if (!checks.length) errors.push('至少8位');
    if (!checks.uppercase) errors.push('大写字母');
    if (!checks.lowercase) errors.push('小写字母');
    if (!checks.number) errors.push('数字');
    if (!checks.special) errors.push('特殊字符');

    if (errors.length > 0) {
      setErrorMessage(`密码需包含: ${errors.join('、')}`);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrorMessage(t('auth:register.confirmPassword.mismatch'));
      return;
    }
    if (!formData.agreeToTerms) {
      setErrorMessage(t('validation:required'));
      return;
    }

    try {
      await register({
        email: formData.email.trim(),
        password: formData.password,
        name: formData.contactName.trim(),
        role: UserRole.ISSUER,
        companyName: formData.companyName.trim() || undefined,
        phone: formData.phone.trim() || undefined,
      }).unwrap();

      // 注册成功，跳转到登录页并带入邮箱
      navigate('/auth/login', {
        state: {
          message: t('auth:register.successMessage'),
          email: formData.email.trim(),
        },
      });
    } catch (error: any) {
      console.error('Registration error:', error);
      const errorMsg =
        error?.data?.error?.message ||
        error?.data?.message ||
        t('auth:register.failed');
      setErrorMessage(errorMsg);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex">
      {/* 左侧品牌展示区域 */}
      <div className="hidden lg:flex lg:w-3/5 xl:w-2/3 relative overflow-hidden">
        {/* 主背景渐变 - 更加柔和优雅 */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-600"></div>

        {/* 次级渐变层 - 增加深度 */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-white/5"></div>

        {/* 精致的装饰性几何图形 */}
        <div className="absolute inset-0">
          {/* 主要装饰圆 */}
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-white/15 rounded-full blur-3xl"></div>

          {/* 次要装饰元素 */}
          <div className="absolute top-1/2 right-1/2 w-32 h-32 bg-white/20 rounded-full blur-2xl"></div>
          <div className="absolute top-3/4 right-1/3 w-24 h-24 bg-white/25 rounded-full blur-xl"></div>

          {/* 网格纹理 */}
          <div className="absolute inset-0 opacity-5">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
        </div>

        {/* 主要内容 - 重新布局 */}
        <div className="relative z-10 flex flex-col justify-center px-16 xl:px-20 text-white max-w-2xl">
          {/* 品牌标识 - 更加精致 */}
          <div className="mb-16">
            <div className="flex items-center mb-8">
              <div className="w-16 h-16 bg-white/25 backdrop-blur-sm rounded-2xl flex items-center justify-center mr-5 border border-white/40 shadow-lg">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <span className="text-2xl">🌱</span>
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight drop-shadow-sm">
                  离岸绿色债券直通车
                </h1>
                <p className="text-white/85 text-base font-light">
                  Offshore Green Bond Express
                </p>
              </div>
            </div>
          </div>

          {/* 注册标题 - 更有冲击力 */}
          <div className="mb-16">
            <div className="space-y-4">
              <h2 className="text-3xl xl:text-4xl font-bold leading-tight tracking-tight drop-shadow-sm">
                加入我们
                <br />
                <span className="bg-gradient-to-r from-white via-emerald-100 to-emerald-200 bg-clip-text text-transparent drop-shadow-none">
                  共创绿色未来
                </span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-white via-white/80 to-white/50 rounded-full shadow-sm"></div>
            </div>
            <p className="text-lg text-white/90 leading-relaxed mt-8 max-w-lg">
              立即注册，获得专业的绿色债券发行服务，
              <span className="font-semibold">开启您的可持续金融之旅</span>
            </p>
          </div>

          {/* 注册优势 - 简化展示 */}
          <div className="space-y-6">
            <div className="flex items-center space-x-4 group cursor-pointer">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30 group-hover:bg-white/35 group-hover:scale-110 transition-all duration-300 shadow-lg">
                <svg
                  className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1 group-hover:text-emerald-100 transition-colors duration-300">
                  安全可靠
                </h3>
                <p className="text-white/80 group-hover:text-white/95 transition-colors duration-300">
                  银行级安全保障，数据加密传输
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4 group cursor-pointer">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30 group-hover:bg-white/35 group-hover:scale-110 transition-all duration-300 shadow-lg">
                <svg
                  className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1 group-hover:text-emerald-100 transition-colors duration-300">
                  快速入驻
                </h3>
                <p className="text-white/80 group-hover:text-white/95 transition-colors duration-300">
                  3分钟完成注册，即刻开始使用
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4 group cursor-pointer">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30 group-hover:bg-white/35 group-hover:scale-110 transition-all duration-300 shadow-lg">
                <svg
                  className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1 group-hover:text-emerald-100 transition-colors duration-300">
                  专业服务
                </h3>
                <p className="text-white/80 group-hover:text-white/95 transition-colors duration-300">
                  专业团队全程指导，贴心服务
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 右侧注册表单区域 - 重新设计 */}
      <div className="lg:w-2/5 xl:w-1/3 flex items-center justify-center px-8 py-12 bg-white/60 backdrop-blur-md">
        <div className="w-full max-w-md">
          {/* 移动端Logo - 优化 */}
          <div className="lg:hidden text-center mb-12">
            <div className="inline-flex items-center justify-center">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                <span className="text-2xl">🌱</span>
              </div>
              <div className="text-left">
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                  离岸绿色债券直通车
                </h1>
                <p className="text-sm text-gray-500 font-medium">
                  Offshore Green Bond Express
                </p>
              </div>
            </div>
          </div>

          {/* 注册卡片 - 全新设计 */}
          <div className="bg-white/98 backdrop-blur-xl rounded-3xl shadow-2xl border border-emerald-300/40 overflow-hidden ring-1 ring-emerald-200/20">
            {/* 卡片头部 - 翡翠绿方案 */}
            <div className="px-8 pt-8 pb-6 bg-gradient-to-r from-emerald-500 to-teal-500">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">
                  发行人注册
                </h2>
                <p className="text-white/90 text-base font-medium">
                  填写以下信息开始使用绿色债券发行服务
                </p>
              </div>
            </div>

            {/* 表单区域 */}
            <div className="px-8 py-8 bg-gradient-to-b from-white to-gray-50/30">
              {/* 错误信息显示 */}
              {errorMessage && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{errorMessage}</p>
                </div>
              )}

              {/* 注册表单 */}
              <form className="space-y-6" onSubmit={handleSubmit}>
                {/* 公司名称 */}
                <div>
                  <label htmlFor="companyName" className="form-label">
                    公司/机构名称
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    type="text"
                    required
                    className="form-input"
                    placeholder="请输入您的公司或机构名称"
                    value={formData.companyName}
                    onChange={handleInputChange}
                  />
                </div>

                {/* 联系人姓名 */}
                <div>
                  <label htmlFor="contactName" className="form-label">
                    联系人姓名
                  </label>
                  <input
                    id="contactName"
                    name="contactName"
                    type="text"
                    required
                    className="form-input"
                    placeholder="请输入联系人姓名"
                    value={formData.contactName}
                    onChange={handleInputChange}
                  />
                </div>

                {/* 邮箱和电话 */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="email" className="form-label">
                      邮箱地址
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="form-input"
                      placeholder="example@company.com"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="form-label">
                      联系电话
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      className="form-input"
                      placeholder="请输入联系电话"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                {/* 密码设置 */}
                <div>
                  <label htmlFor="password" className="form-label">
                    设置密码
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      className="form-input pr-12"
                      placeholder="请设置登录密码"
                      value={formData.password}
                      onChange={handleInputChange}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>

                  {/* 密码强度指示器 */}
                  {formData.password &&
                    (() => {
                      const { checks, strength } = checkPasswordStrength(
                        formData.password
                      );
                      const getStrengthColor = () => {
                        if (strength <= 40) return 'bg-red-500';
                        if (strength <= 60) return 'bg-yellow-500';
                        if (strength <= 80) return 'bg-green-500';
                        return 'bg-emerald-600';
                      };
                      const getStrengthText = () => {
                        if (strength <= 40) return '弱';
                        if (strength <= 60) return '中';
                        if (strength <= 80) return '强';
                        return '非常强';
                      };
                      const getTextColor = () => {
                        if (strength <= 40) return 'text-red-600';
                        if (strength <= 60) return 'text-yellow-600';
                        if (strength <= 80) return 'text-green-600';
                        return 'text-emerald-600';
                      };

                      return (
                        <div className="mt-2">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor()}`}
                                style={{ width: `${strength}%` }}
                              />
                            </div>
                            <span
                              className={`text-sm font-medium ${getTextColor()}`}
                            >
                              {getStrengthText()}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-1 text-xs">
                            <div
                              className={`flex items-center gap-1 ${checks.length ? 'text-green-600' : 'text-gray-400'}`}
                            >
                              {checks.length ? (
                                <CheckCircleIcon className="h-4 w-4" />
                              ) : (
                                <XCircleIcon className="h-4 w-4" />
                              )}
                              至少8位
                            </div>
                            <div
                              className={`flex items-center gap-1 ${checks.uppercase ? 'text-green-600' : 'text-gray-400'}`}
                            >
                              {checks.uppercase ? (
                                <CheckCircleIcon className="h-4 w-4" />
                              ) : (
                                <XCircleIcon className="h-4 w-4" />
                              )}
                              大写字母
                            </div>
                            <div
                              className={`flex items-center gap-1 ${checks.lowercase ? 'text-green-600' : 'text-gray-400'}`}
                            >
                              {checks.lowercase ? (
                                <CheckCircleIcon className="h-4 w-4" />
                              ) : (
                                <XCircleIcon className="h-4 w-4" />
                              )}
                              小写字母
                            </div>
                            <div
                              className={`flex items-center gap-1 ${checks.number ? 'text-green-600' : 'text-gray-400'}`}
                            >
                              {checks.number ? (
                                <CheckCircleIcon className="h-4 w-4" />
                              ) : (
                                <XCircleIcon className="h-4 w-4" />
                              )}
                              数字
                            </div>
                            <div
                              className={`flex items-center gap-1 ${checks.special ? 'text-green-600' : 'text-gray-400'}`}
                            >
                              {checks.special ? (
                                <CheckCircleIcon className="h-4 w-4" />
                              ) : (
                                <XCircleIcon className="h-4 w-4" />
                              )}
                              特殊字符
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                </div>

                {/* 确认密码 */}
                <div>
                  <label htmlFor="confirmPassword" className="form-label">
                    确认密码
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      className="form-input pr-12"
                      placeholder="请再次输入密码"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                {/* 服务条款同意 */}
                <div className="flex items-start">
                  <input
                    id="agreeToTerms"
                    name="agreeToTerms"
                    type="checkbox"
                    required
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                    checked={formData.agreeToTerms}
                    onChange={handleInputChange}
                  />
                  <label
                    htmlFor="agreeToTerms"
                    className="ml-3 text-sm text-gray-700"
                  >
                    我已阅读并同意{' '}
                    <Link
                      to="/terms"
                      className="text-primary-600 hover:text-primary-500 font-medium"
                    >
                      服务条款
                    </Link>{' '}
                    和{' '}
                    <Link
                      to="/privacy"
                      className="text-primary-600 hover:text-primary-500 font-medium"
                    >
                      隐私政策
                    </Link>
                  </label>
                </div>

                {/* 注册按钮 */}
                <div>
                  <button
                    type="submit"
                    disabled={isLoading || !formData.agreeToTerms}
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02]"
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        注册中...
                      </div>
                    ) : (
                      '立即注册'
                    )}
                  </button>
                </div>

                {/* 登录链接 */}
                <div className="text-center mt-8">
                  <p className="text-gray-500 text-base">
                    已有账户？{' '}
                    <Link
                      to="/auth/login"
                      className="text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
                    >
                      立即登录
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </div>

          {/* 底部版权和版本信息 */}
          <div className="text-center mt-8 space-y-3">
            {/* 版本号显示 */}
            <div className="flex items-center justify-center space-x-3">
              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-lg">
                <div className="w-5 h-5 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded flex items-center justify-center">
                  <span className="text-white text-xs font-bold">GB</span>
                </div>
                <span className="text-sm font-semibold text-emerald-600">
                  {VersionInfo.displayVersion}
                </span>
              </div>
              <div
                className="px-2 py-1 rounded text-xs font-medium"
                style={{
                  backgroundColor: VersionInfo.statusColors.bg,
                  color: VersionInfo.statusColors.text,
                  border: `1px solid ${VersionInfo.statusColors.border}`,
                }}
              >
                {VersionInfo.status}
              </div>
            </div>

            {/* 版权信息 */}
            <p className="text-gray-400 text-sm">
              {t('common:app.copyright', { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
